// Active Care Plan Print View
import React, { useEffect } from "react";
// import { logPrintLanguage } from "../../../services/dashboardServices";
import { formatDate } from "../../../utils/dateUtils";
import { useCookieLanguageLogger } from "../../../hooks/useCookieLanguageLogger";
import UrgentCare from "./UrgentCare";
import Spacer from "../../common/Spacer";
import GlobalPrintStyle from "../../common/GlobalPrintStyle";
import Avatar from "../../common/Avatar";
import ReactQRCode from "react-qr-code";
import { useGoogleTranslate } from "../../../utils/googleTranslate";
import ItemThumbnail from "../../common/ItemThumbnail";

import {
  OuterWrapper,
  HeaderWrapper,
  HeaderContainer,
  TextOverlay,
  TextContainer,
  BottomTextContainer,
  BottomTextColumn,
  HeaderQRContainer,
  HeaderQRCodeLabel,
  BodyContainer,
  SectionContainer,
  SectionTitle,
  QRCodeWrapper,
  QRCodeLabel,
  SectionCard,
  ItemCardWrapper,
  ItemCard,
  ItemContent,
  ImportantInfoContainer,
  FooterContainer,
  ContributersWrapper,
  Contributers,
  PeopleContainerWrapper,
  PeopleContainer,
  UserCard,
  UserDetails,
  DigitalOnly,
} from "../../common/PrintHelper";

const PrintActiveCarePlan = ({ plan }) => {
  const {
    sections = [],
    contacts = [],
    nhsNumber,
    updated_at,
    mdtUsers = [],
    important_info,
  } = plan;

  useCookieLanguageLogger(plan.public_id);

  useEffect(() => {
    // small timeout gives React a chance to flush all DOM updates
    const tid = setTimeout(() => window.print(), 200);
    return () => clearTimeout(tid);
  }, []);

  useGoogleTranslate(true);
  console.log("Printing care plan:", plan);

  return (
    <>
      <DigitalOnly>
        {/* the hidden translator container */}
        <div id="google_translate_element"></div>
      </DigitalOnly>

      <OuterWrapper>
        <GlobalPrintStyle />
        <HeaderWrapper>
          <HeaderContainer>
            <TextOverlay>
              <TextContainer>
                <img src="/assets/SidebarLogoImage.svg" alt="Sidebar Logo" />

                <BottomTextContainer>
                  <BottomTextColumn>
                    <p className="body-text-small medium secondary-700">
                      NHS number
                    </p>
                    <p className="body-text-regular semi-bold neutral-700">
                      {nhsNumber}
                    </p>

                    <Spacer height="8px" />
                    <p className="body-text-small medium secondary-700">Name</p>
                  </BottomTextColumn>
                  <BottomTextColumn>
                    <p className="body-text-small medium secondary-700">
                      Last updated
                    </p>
                    <p className="body-text-regular semi-bold neutral-700">
                      {formatDate(updated_at)}
                    </p>
                  </BottomTextColumn>
                </BottomTextContainer>
              </TextContainer>
            </TextOverlay>

            <HeaderQRContainer>
              <ReactQRCode
                value={`${window.location.origin}/share/${plan.public_id}/`}
                size={100}
              />
              <HeaderQRCodeLabel>
                <p className="body-text-regular medium secondary-800">
                  Scan to access the latest digital care plan
                </p>
              </HeaderQRCodeLabel>
            </HeaderQRContainer>
          </HeaderContainer>
        </HeaderWrapper>

        <BodyContainer>
          {sections
            .filter((section) =>
              section.items.some((item) => item.answer?.trim())
            )
            .map((section, index) => (
              <SectionContainer $isFirst={index === 0} key={section.id}>
                <SectionTitle color={section.color}>
                  <h4 className="semi-bold neutral-100">{section.title}</h4>
                </SectionTitle>
                {section.qr_code_url && (
                  <QRCodeWrapper>
                    <img src={section.qr_code_url} alt="QR Code" />
                    <QRCodeLabel>
                      {section.qr_description
                        ? section.qr_description
                        : "Scan for more details"}
                    </QRCodeLabel>
                  </QRCodeWrapper>
                )}
                <Spacer height="16px" />
                <SectionCard>
                  {section.items.map((item) => (
                    <ItemCardWrapper key={item.id}>
                      <ItemCard>
                        <ItemThumbnail
                          src={item?.image_url}
                          alt={item?.image_alt_text || item?.title}
                          width={"150px"}
                          height={"150px"}
                        />
                        <ItemContent>
                          <h6 className="semi-bold">{item.title}</h6>
                          <p>{item.answer || "No answer provided"}</p>
                          {/* If there's a QR code, only show it in the printed version */}
                          {item.qr_code_url && (
                            <QRCodeWrapper>
                              <img src={item.qr_code_url} alt="QR Code" />
                              <QRCodeLabel>Scan for more details</QRCodeLabel>
                            </QRCodeWrapper>
                          )}
                        </ItemContent>
                      </ItemCard>
                    </ItemCardWrapper>
                  ))}
                </SectionCard>
                <Spacer height="16px" />
              </SectionContainer>
            ))}

          <ImportantInfoContainer>
            <UrgentCare
              retrievedContacts={contacts}
              mode="view"
              initialImportantInfo={important_info}
            />
          </ImportantInfoContainer>
        </BodyContainer>

        <FooterContainer>
          <ContributersWrapper>
            <Contributers>
              <PeopleContainerWrapper>
                <h6 className="semi-bold">Care plan collaborators</h6>
                <PeopleContainer>
                  {mdtUsers.length > 0 ? (
                    mdtUsers.map((user) => (
                      <UserCard key={user.id}>
                        <Avatar
                          profilePictureUrl={user.profile_picture}
                          name={user.name}
                        />

                        <UserDetails>
                          <p className="body-text-regular semi-bold neutral-900">
                            {user.name}
                          </p>
                          <p className="body-text-small regular neutral-900">
                            {user.roles[0].name || "Team member"}
                          </p>
                        </UserDetails>
                      </UserCard>
                    ))
                  ) : (
                    <p>No collaborators listed</p>
                  )}
                </PeopleContainer>
              </PeopleContainerWrapper>
            </Contributers>
          </ContributersWrapper>
        </FooterContainer>
      </OuterWrapper>
    </>
  );
};

export default PrintActiveCarePlan;

/* Styled components remain unchanged */

/* The main container that holds the entire care plan content */

/* 
  We will wrap QRCodeWrapper with <PrintOnly> to ensure
  it's only visible in printed version.
*/
